import React, { useEffect, useState } from 'react';
import { Globe, Loader2, CheckCircle2, Upload, ExternalLink } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/i18n/LanguageContext';
import { fetchMyWebsiteRequest, uploadLogo, WebsiteRequest, WEBSITE_STATUS_LABEL, WEBSITE_STATUSES } from '@/lib/websiteRequests';

/** Shown only when the server opened a free-website request for this member (Premium). */
const WebsiteRequestCard: React.FC = () => {
  const { profile } = useAuth();
  const { lang } = useI18n();
  const es = lang === 'es';
  const [req, setReq] = useState<WebsiteRequest | null>(null);
  const [form, setForm] = useState({ business_name: '', services: '', phone: '', logo_url: '' });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    fetchMyWebsiteRequest(profile?.id).then((r) => {
      setReq(r);
      if (r) setForm({ business_name: r.business_name || r.company_name || '', services: r.services || '', phone: r.phone || '', logo_url: r.logo_url || '' });
    });
  }, [profile?.id]);

  if (!req) return null;
  const st = WEBSITE_STATUS_LABEL[req.status] || WEBSITE_STATUS_LABEL.requested;
  const step = WEBSITE_STATUSES.indexOf(req.status);
  const showForm = !req.intake_submitted_at || editing;
  const input = 'mt-1 w-full min-h-[48px] rounded-xl border border-slate-300 px-3 text-base focus:border-orange-500 focus:outline-none';

  const onLogo = async (f?: File | null) => {
    if (!f || !profile?.id) return;
    if (f.size > 5 * 1024 * 1024) { setMsg({ ok: false, text: es ? 'El logo debe pesar menos de 5 MB.' : 'Logo must be under 5 MB.' }); return; }
    setUploading(true); setMsg(null);
    try { const url = await uploadLogo(profile.id, f); setForm((p) => ({ ...p, logo_url: url })); }
    catch { setMsg({ ok: false, text: es ? 'No se pudo subir el logo. Intente de nuevo.' : 'Logo upload failed. Please try again.' }); }
    setUploading(false);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.business_name.trim() || !form.services.trim()) {
      setMsg({ ok: false, text: es ? 'Escriba el nombre del negocio y sus servicios.' : 'Please enter your business name and services.' });
      return;
    }
    setBusy(true); setMsg(null);
    const patch = { business_name: form.business_name.trim(), services: form.services.trim(), phone: form.phone.trim() || null, logo_url: form.logo_url || null, intake_submitted_at: new Date().toISOString() };
    const { data, error } = await supabase.from('mkt_website_requests').update(patch).eq('id', req.id).select('*').maybeSingle();
    setBusy(false);
    if (error || !data) { setMsg({ ok: false, text: es ? 'No se pudo guardar. Intente de nuevo.' : 'Could not save. Please try again.' }); return; }
    setReq(data as WebsiteRequest); setEditing(false);
    setMsg({ ok: true, text: es ? 'Recibido. Nuestro equipo empezará su sitio web.' : 'Received. Our team will start building your website.' });
  };

  return (
    <section className="mb-8 rounded-2xl border-2 border-violet-300 bg-violet-50 p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-extrabold text-slate-900 sm:text-xl">
          <Globe className="h-5 w-5 text-violet-600" /> {es ? 'Su sitio web gratis' : 'Your free website'}
        </h2>
        <span className={`rounded-full px-3 py-1 text-xs font-black ${st.cls}`}>{es ? st.es : st.en}</span>
      </div>
      <p className="mt-1 text-sm text-slate-700">{es ? 'Incluido con su membresía Premium.' : 'Included with your Premium membership.'}</p>

      <ol className="mt-3 grid grid-cols-3 gap-2">
        {WEBSITE_STATUSES.map((s, i) => (
          <li key={s} className={`rounded-lg px-2 py-2 text-center text-[11px] font-bold sm:text-xs ${i <= step ? 'bg-violet-600 text-white' : 'bg-white text-slate-400'}`}>
            {es ? WEBSITE_STATUS_LABEL[s].es : WEBSITE_STATUS_LABEL[s].en}
          </li>
        ))}
      </ol>

      {req.status === 'delivered' && req.website_url && (
        <a href={req.website_url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 text-sm font-bold text-white hover:bg-emerald-700 sm:w-auto">
          {es ? 'Ver mi sitio web' : 'View my website'} <ExternalLink className="h-4 w-4" />
        </a>
      )}

      {showForm ? (
        <form onSubmit={save} className="mt-4 space-y-3 rounded-xl bg-white p-4">
          <p className="text-sm font-bold text-slate-800">{es ? 'Cuéntenos sobre su negocio (2 minutos)' : 'Tell us about your business (2 minutes)'}</p>
          <label className="block text-sm font-semibold text-slate-700">{es ? 'Nombre del negocio' : 'Business name'}
            <input className={input} value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} required />
          </label>
          <label className="block text-sm font-semibold text-slate-700">{es ? 'Servicios que ofrece' : 'Services you offer'}
            <textarea className={`${input} min-h-[96px] py-2`} value={form.services} onChange={(e) => setForm({ ...form, services: e.target.value })} placeholder={es ? 'Ej.: techos, reparaciones, remodelaciones' : 'e.g. roofing, repairs, remodels'} required />
          </label>
          <label className="block text-sm font-semibold text-slate-700">{es ? 'Teléfono para el sitio' : 'Phone for the website'}
            <input type="tel" className={input} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </label>
          <div>
            <span className="block text-sm font-semibold text-slate-700">{es ? 'Logo (opcional)' : 'Logo (optional)'}</span>
            <div className="mt-1 flex items-center gap-3">
              {form.logo_url && <img src={form.logo_url} alt="Logo" className="h-12 w-12 rounded-lg border object-contain" />}
              <label className="inline-flex min-h-[48px] cursor-pointer items-center gap-2 rounded-xl border-2 border-dashed border-slate-300 px-4 text-sm font-bold text-slate-700 hover:border-violet-400">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {form.logo_url ? (es ? 'Cambiar logo' : 'Change logo') : (es ? 'Subir logo' : 'Upload logo')}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => onLogo(e.target.files?.[0])} />
              </label>
            </div>
          </div>
          <button type="submit" disabled={busy || uploading} className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-violet-600 px-5 text-sm font-bold text-white hover:bg-violet-700 disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {es ? 'Enviar información' : 'Send details'}
          </button>
        </form>
      ) : (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-white p-4 text-sm">
          <span className="flex items-center gap-2 font-semibold text-slate-700"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> {es ? 'Información recibida' : 'Details received'} — {req.business_name}</span>
          {req.status !== 'delivered' && (
            <button type="button" onClick={() => setEditing(true)} className="min-h-[44px] text-sm font-bold text-violet-700">{es ? 'Editar' : 'Edit'}</button>
          )}
        </div>
      )}
      {msg && <p className={`mt-2 text-sm font-semibold ${msg.ok ? 'text-emerald-700' : 'text-red-600'}`}>{msg.text}</p>}
    </section>
  );
};

export default WebsiteRequestCard;
